import { SideBarProvider } from '@exam-management/app-frame';
import { theme } from '@exam-management/common/theme';
import { ConfigProvider } from '@exam-management/configuration';
import { Routes } from '@generouted/react-router';
import * as Sentry from '@sentry/react';
import { ApolloProvider, createApolloClient } from '@shared/apollo';
import { AuthenticationProvider, getExternalForceLogout } from '@shared/auth';
import { CssBaseline, ThemeProvider } from '@shared/components';
import { LoggerProvider } from '@shared/logger';
import { PermissionProvider } from '@shared/permissions';
import { SnackbarProvider } from '@shared/snackbar';
import { NuqsAdapter } from 'nuqs/adapters/react-router';
import { StrictMode, useMemo } from 'react';
import * as ReactDOM from 'react-dom/client';

import { configuration } from './configuration';

const dsn = import.meta.env.VITE_SENTRY_DSN;

if (dsn && !import.meta.env.DEV) {
  Sentry.init({
    dsn,
    sendDefaultPii: true,
    environment: import.meta.env.MODE,
  });
}

const { appShell, graphql, logLevel } = configuration;
const defaultFetchPolicy = graphql.defaultFetchPolicy ?? 'cache-first';

const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);

function App() {
  const client = useMemo(
    () =>
      createApolloClient(
        graphql.apiUrl,
        {
          query: { fetchPolicy: defaultFetchPolicy },
          watchQuery: { fetchPolicy: defaultFetchPolicy },
        },
        {
          onUnauthorized: () => getExternalForceLogout()?.(),
        },
      ),
    [graphql.apiUrl],
  );
  return (
    <ConfigProvider config={configuration}>
      <ApolloProvider client={client}>
        <LoggerProvider config={{ level: logLevel, enableConsole: true }}>
          <ThemeProvider theme={theme}>
            <SnackbarProvider
              options={{
                snackbarVariant: {
                  pending: 'muted',
                },
              }}
            >
              <AuthenticationProvider>
                <PermissionProvider>
                  <NuqsAdapter>
                    <SideBarProvider appShell={appShell}>
                      <CssBaseline />
                      <Routes />
                    </SideBarProvider>
                  </NuqsAdapter>
                </PermissionProvider>
              </AuthenticationProvider>
            </SnackbarProvider>
          </ThemeProvider>
        </LoggerProvider>
      </ApolloProvider>
    </ConfigProvider>
  );
}

root.render(
  <StrictMode>
    <App />
  </StrictMode>,
);
